import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { TemplateSection, TemplateField, TemplateItemField, ReportTemplate, SearchParameterType } from '../../types';
import { apiService, apiAdmin } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { ParameterPickerModal, ParameterItem } from './ParameterPickerModal';

interface TemplateBuilderProps {
  template?: ReportTemplate;
  parameterTypeId?: number;
  apiResults?: any[]; // Search results to pre-populate fields
  onSave: (template: Partial<ReportTemplate>) => Promise<void>;
  onCancel: () => void;
}

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  template,
  parameterTypeId,
  apiResults = [],
  onSave,
  onCancel
}) => {
  const { showToast } = useToast();

  /** Get numeric parameter type id from template (API may return id or nested object). */
  const getTemplateParameterTypeId = (t: ReportTemplate | undefined): number => {
    if (!t) return parameterTypeId || 0;
    const pt = t.parameter_type;
    if (typeof pt === 'number') return pt;
    if (pt && typeof pt === 'object' && 'id' in pt) return Number((pt as { id: number }).id);
    return (t as { parameter_type_id?: number }).parameter_type_id ?? parameterTypeId ?? 0;
  };

  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [isActive, setIsActive] = useState<boolean>(template?.is_active !== false);
  const [isDefault, setIsDefault] = useState<boolean>(template?.is_default || false);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedParameterType, setSelectedParameterType] = useState<number>(
    getTemplateParameterTypeId(template) || parameterTypeId || 0
  );
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [parameterTypesLoading, setParameterTypesLoading] = useState(true);
  const [sections, setSections] = useState<TemplateSection[]>(
    template?.configuration?.sections || []
  );
  const [availableFields, setAvailableFields] = useState<Record<string, string[]>>({});
  const [availableApis, setAvailableApis] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [apisLoading, setApisLoading] = useState(false);
  const [apiDisplayParams, setApiDisplayParams] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerTargetSectionId, setPickerTargetSectionId] = useState<string | undefined>(undefined);
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(new Set());
  const [bulkTargetSectionId, setBulkTargetSectionId] = useState<string>('');
  const [fieldVisibilityFilter, setFieldVisibilityFilter] = useState<'all' | 'hide_checked' | 'unhide_checked'>('all');
  const [sectionApiFilter, setSectionApiFilter] = useState<number | ''>('');
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(new Set());
  const [dragData, setDragData] = useState<{
    type: 'field';
    fieldId: string;
    fromSectionId: string;
  } | {
    type: 'section';
    sectionId: string;
  } | null>(null);

  const fieldVisibilityCounts = useMemo(() => {
    let all = 0;
    let hideChecked = 0;
    let unhideChecked = 0;
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        all += 1;
        if (field.hidden_in_report) {
          hideChecked += 1;
        } else {
          unhideChecked += 1;
        }
      });
    });
    return { all, hideChecked, unhideChecked };
  }, [sections]);

  const matchesFieldVisibilityFilter = (field: TemplateField): boolean => {
    const isHidden = field.hidden_in_report ?? false;
    if (fieldVisibilityFilter === 'hide_checked') return isHidden;
    if (fieldVisibilityFilter === 'unhide_checked') return !isHidden;
    return true;
  };

  const relevantApiIds = useMemo(
    () => new Set(availableApis.map((api) => api.id)),
    [availableApis]
  );

  const relevantApiNames = useMemo(
    () => new Set(availableApis.map((api) => api.name)),
    [availableApis]
  );

  const isFieldApiRelevant = (field: TemplateField): boolean => {
    if (field.api_source_id != null) {
      return relevantApiIds.has(field.api_source_id);
    }
    if (field.api_source) {
      return relevantApiNames.has(field.api_source);
    }
    return true;
  };

  const filteredSections = useMemo(() => {
    if (!sectionApiFilter) return sections;
    const api = availableApis.find((a) => a.id === sectionApiFilter);
    return sections.filter((section) => {
      const sectionApiId = (section as TemplateSection & { api_source_id?: number }).api_source_id;
      if (sectionApiId === sectionApiFilter) return true;
      return section.fields.some(
        (field) =>
          field.api_source_id === sectionApiFilter ||
          (api && field.api_source === api.name)
      );
    });
  }, [sections, sectionApiFilter, availableApis]);

  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const expandAllSections = () => {
    setExpandedSectionIds(new Set(sections.map((s, idx) => s.id || `section_${idx}`)));
  };

  const collapseAllSections = () => {
    setExpandedSectionIds(new Set());
  };

  useEffect(() => {
    fetchParameterTypes();
  }, []);

  // Update state when template prop changes (for editing).
  // Stable section/field ids — do NOT depend on availableApis or Date.now(), or Expand state resets.
  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setDescription(template.description || '');
      setIsActive(template.is_active !== false);
      setIsDefault(template.is_default || false);
      setIsDirty(false);
      setSelectedParameterType(getTemplateParameterTypeId(template) || parameterTypeId || 0);
      setExpandedSectionIds(new Set());

      const templateSections = template.configuration?.sections || [];
      const sectionsWithIds = templateSections.map((section: any, index: number) => ({
        ...section,
        id: section.id || `section_${index}`,
        fields: (section.fields || []).map((field: any, fieldIndex: number) => ({
          ...field,
          id: field.id || `field_${index}_${fieldIndex}`,
          hidden_in_report: field.hidden_in_report ?? false,
        })),
      }));
      setSections(sectionsWithIds);

      const fieldsFromTemplate: Record<string, string[]> = {};
      templateSections.forEach((section: any) => {
        (section.fields || []).forEach((field: any) => {
          const apiSource = field.api_source;
          const fieldPath = field.field_path;
          if (apiSource && fieldPath) {
            if (!fieldsFromTemplate[apiSource]) {
              fieldsFromTemplate[apiSource] = [];
            }
            if (!fieldsFromTemplate[apiSource].includes(fieldPath)) {
              fieldsFromTemplate[apiSource].push(fieldPath);
            }
          }
        });
      });

      setAvailableFields(prev => {
        const merged: Record<string, string[]> = { ...prev };
        Object.keys(fieldsFromTemplate).forEach(apiName => {
          merged[apiName] = Array.from(new Set([
            ...(merged[apiName] || []),
            ...fieldsFromTemplate[apiName]
          ]));
        });
        return merged;
      });
    } else {
      setName('');
      setDescription('');
      setSelectedParameterType(parameterTypeId || 0);
      setSections([]);
      setExpandedSectionIds(new Set());
    }
  }, [template, parameterTypeId]);

  // Backfill api_source_id once APIs load — without regenerating section ids
  useEffect(() => {
    if (availableApis.length === 0) return;
    setSections(prev => {
      let changed = false;
      const next = prev.map(section => ({
        ...section,
        fields: section.fields.map(field => {
          if (field.api_source_id != null) return field;
          if (!field.api_source) return field;
          const resolvedId = availableApis.find(a => a.name === field.api_source)?.id;
          if (resolvedId == null) return field;
          changed = true;
          return { ...field, api_source_id: resolvedId };
        }),
      }));
      return changed ? next : prev;
    });
  }, [availableApis]);

  useEffect(() => {
    if (selectedParameterType > 0) {
      fetchAvailableApis(selectedParameterType);
      setSectionApiFilter('');
    } else {
      setAvailableApis([]);
    }
  }, [selectedParameterType]);

  useEffect(() => {
    if (apiResults && apiResults.length > 0) {
      extractAvailableFields();
    } else {
      // Initialize available fields from API list even without results
      initializeFieldsFromApis();
    }
  }, [apiResults, availableApis]);

  const fetchParameterTypes = async () => {
    try {
      setParameterTypesLoading(true);
      const types = await apiService.getParameterTypes();
      setParameterTypes(types);
    } catch (err) {
      showToast('Failed to load parameter types', 'error');
    } finally {
      setParameterTypesLoading(false);
    }
  };

  const fetchAvailableApis = async (paramTypeId: number) => {
    try {
      setApisLoading(true);
      const apis = await apiAdmin.listApis({ parameter_type_id: paramTypeId, status: 'active' });
      const normalized = (apis || []).map((a: any) => ({
        id: a.id,
        name: a.name || a.api_name || `API #${a.id}`,
        description: (a.description || '').trim(),
      }));
      setAvailableApis(normalized);
      // Pre-load display parameters for all APIs so the parameter picker has them ready
      normalized.forEach((api: any) => {
        fetchApiDisplayParams(api.name);
      });
    } catch (err) {
      console.error('Failed to load APIs:', err);
      setAvailableApis([]);
    } finally {
      setApisLoading(false);
    }
  };

  const fetchApiDisplayParams = async (apiName: string) => {
    const api = availableApis.find(a => a.name === apiName);
    if (!api) return;

    // Check if already loaded
    if (apiDisplayParams[apiName]) {
      return apiDisplayParams[apiName];
    }

    try {
      const apiDetails = await apiAdmin.getApi(api.id);
      if (apiDetails.display_parameters && typeof apiDetails.display_parameters === 'object' && !Array.isArray(apiDetails.display_parameters)) {
        // Filter and normalize display_parameters - only keep string values
        const normalizedParams: Record<string, string> = {};
        for (const [key, value] of Object.entries(apiDetails.display_parameters)) {
          // Skip if key is empty or value is not a string
          if (!key || typeof value !== 'string') {
            continue;
          }
          
          // Only include if value is a non-empty string
          const trimmedValue = value.trim();
          if (trimmedValue.length > 0) {
            normalizedParams[key] = trimmedValue;
          }
        }
        
        if (Object.keys(normalizedParams).length > 0) {
          // Store normalized display_parameters for this API
          setApiDisplayParams(prev => ({
            ...prev,
            [apiName]: normalizedParams
          }));
          
          // Also add display_parameters field paths to availableFields
          const fieldPaths = Object.keys(normalizedParams);
          setAvailableFields(prev => ({
            ...prev,
            [apiName]: Array.from(new Set([...prev[apiName] || [], ...fieldPaths]))
          }));
          
          return normalizedParams;
        }
      }
    } catch (err) {
      console.warn(`Failed to fetch display_parameters for API ${apiName}:`, err);
    }
    return null;
  };

  const initializeFieldsFromApis = () => {
    // Initialize available fields with API names, even without data
    const fields: Record<string, string[]> = {};
    availableApis.forEach((api) => {
      fields[api.name] = []; // Empty array - user will need to enter field path manually or type the path
    });
    if (Object.keys(fields).length > 0) {
      setAvailableFields(fields);
    }
  };

  const extractAvailableFields = () => {
    const fields: Record<string, string[]> = {};
    
    // First, add all available APIs from database
    availableApis.forEach((api) => {
      fields[api.name] = []; // Initialize with empty array
    });
    
    // Then, add fields from search results if available
    apiResults.forEach((result) => {
      if (result.status === 'success' && result.data) {
        const apiName = result.api_name || `API ${result.api_id}`;
        // If API exists in availableApis, use the name from there
        const matchedApi = availableApis.find(a => 
          a.name === apiName || 
          a.id === result.api_id ||
          `API ${a.id}` === apiName ||
          `API ${result.api_id}` === apiName
        );
        const finalApiName = matchedApi ? matchedApi.name : apiName;
        
        if (!fields[finalApiName]) {
          fields[finalApiName] = [];
        }
        // Merge extracted fields
        const extractedFields = flattenObjectKeys(result.data);
        fields[finalApiName] = Array.from(new Set([...fields[finalApiName], ...extractedFields]));
      }
    });
    
    setAvailableFields(fields);
  };

  const flattenObjectKeys = (obj: any, prefix = ''): string[] => {
    const keys: string[] = [];
    
    if (typeof obj !== 'object' || obj === null) {
      return keys;
    }
    
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}_${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        keys.push(...flattenObjectKeys(value, fullKey));
      } else if (Array.isArray(value) && value.length > 0) {
        // Handle arrays - use index 0 for pattern
        const arrayPrefix = `${fullKey}_0`;
        value.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            keys.push(...flattenObjectKeys(item, arrayPrefix.replace('_0', `_${index}`)));
          } else {
            // For simple array items, just use the array key
            if (index === 0) {
              keys.push(arrayPrefix.replace('_0', ''));
            }
          }
        });
      } else {
        keys.push(fullKey);
      }
    }
    
    return keys;
  };

  const addSection = () => {
    setIsDirty(true);
    const newSection: TemplateSection = {
      id: `section_${Date.now()}`,
      heading: `Section ${sections.length + 1}`,
      order: sections.length + 1,
      collapsible: false,
      fields: []
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (index: number, updates: Partial<TemplateSection>) => {
    setIsDirty(true);
    setSections(prev => prev.map((s, i) => 
      i === index ? { ...s, ...updates } : s
    ));
  };

  const deleteSection = (index: number) => {
    const sec = sections[index];
    if (!window.confirm(`Are you sure you want to delete section "${sec?.heading}"?`)) {
      return;
    }
    setIsDirty(true);
    setSections(prev => {
      const newSections = prev.filter((_, i) => i !== index);
      newSections.forEach((s, i) => { s.order = i + 1; });
      return newSections;
    });
    showToast('Section deleted', 'info');
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    setIsDirty(true);
    setSections(prev => {
      const newSections = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex < 0 || newIndex >= newSections.length) return prev;
      
      [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
      // Update orders
      newSections.forEach((s, i) => { s.order = i + 1; });
      
      return newSections;
    });
  };

  const handleAddFieldsFromPicker = (
    targetSectionId: string | 'NEW_SECTION',
    fieldsToAdd: ParameterItem[],
    newSectionHeading?: string
  ) => {
    if (fieldsToAdd.length === 0) return;

    setSections(prev => {
      if (targetSectionId === 'NEW_SECTION') {
        const newSec: TemplateSection = {
          id: `section_${Date.now()}`,
          heading: newSectionHeading || `Section ${prev.length + 1}`,
          order: prev.length + 1,
          collapsible: false,
          fields: fieldsToAdd.map((item, idx) => ({
            id: `field_${Date.now()}_${idx}`,
            api_source: item.api_source,
            api_source_id: item.api_source_id,
            field_path: item.field_path,
            display_name: item.display_name,
            order: idx + 1,
            merge_strategy: 'first',
            hidden_in_report: false
          }))
        };
        return [...prev, newSec];
      }

      return prev.map(sec => {
        if (sec.id !== targetSectionId) return sec;
        const newFields: TemplateField[] = fieldsToAdd.map((item, idx) => ({
          id: `field_${Date.now()}_${idx}`,
          api_source: item.api_source,
          api_source_id: item.api_source_id,
          field_path: item.field_path,
          display_name: item.display_name,
          order: sec.fields.length + idx + 1,
          merge_strategy: 'first',
          hidden_in_report: false
        }));
        return {
          ...sec,
          fields: [...sec.fields, ...newFields]
        };
      });
    });

    setIsDirty(true);
    showToast(`Added ${fieldsToAdd.length} parameter(s) to section`, 'success');
  };

  const deleteField = (sectionIndex: number, fieldIndex: number) => {
    setIsDirty(true);
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const newFields = s.fields.filter((_, j) => j !== fieldIndex);
      newFields.forEach((f, j) => { f.order = j + 1; });
      return { ...s, fields: newFields };
    }));
  };

  const toggleSelectField = (fieldId: string) => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const handleSelectAllSectionFields = (section: TemplateSection) => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);
      const allSelected = section.fields.length > 0 && section.fields.every((f, idx) => next.has(f.id || `field_${idx}`));
      if (allSelected) {
        section.fields.forEach((f, idx) => next.delete(f.id || `field_${idx}`));
      } else {
        section.fields.forEach((f, idx) => next.add(f.id || `field_${idx}`));
      }
      return next;
    });
  };

  const handleBulkMoveFields = () => {
    if (!bulkTargetSectionId || selectedFieldIds.size === 0) return;

    setIsDirty(true);
    setSections(prev => {
      const movedFields: TemplateField[] = [];

      const updated = prev.map(sec => {
        const remainingFields = sec.fields.filter((f, idx) => {
          const key = f.id || `field_${idx}`;
          if (selectedFieldIds.has(key)) {
            movedFields.push(f);
            return false;
          }
          return true;
        });
        remainingFields.forEach((f, idx) => { f.order = idx + 1; });
        return { ...sec, fields: remainingFields };
      });

      const targetSec = updated.find(s => s.id === bulkTargetSectionId);
      if (targetSec) {
        targetSec.fields = [...targetSec.fields, ...movedFields];
        targetSec.fields.forEach((f, idx) => { f.order = idx + 1; });
      }

      return updated;
    });

    showToast(`Moved ${selectedFieldIds.size} field(s)`, 'success');
    setSelectedFieldIds(new Set());
    setBulkTargetSectionId('');
  };

  const handleBulkDeleteFields = () => {
    if (selectedFieldIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedFieldIds.size} selected field(s)?`)) return;

    setIsDirty(true);
    setSections(prev => prev.map(sec => {
      const remainingFields = sec.fields.filter((f, idx) => !selectedFieldIds.has(f.id || `field_${idx}`));
      remainingFields.forEach((f, idx) => { f.order = idx + 1; });
      return { ...sec, fields: remainingFields };
    }));

    showToast(`Deleted ${selectedFieldIds.size} field(s)`, 'success');
    setSelectedFieldIds(new Set());
  };

  const handleBulkToggleHide = (hide: boolean) => {
    if (selectedFieldIds.size === 0) return;

    setIsDirty(true);
    setSections(prev => prev.map(sec => ({
      ...sec,
      fields: sec.fields.map((f, idx) => {
        if (selectedFieldIds.has(f.id || `field_${idx}`)) {
          return { ...f, hidden_in_report: hide };
        }
        return f;
      })
    })));

    showToast(`${hide ? 'Hidden' : 'Unhidden'} ${selectedFieldIds.size} field(s)`, 'success');
  };

  const addField = (sectionIndex: number) => {
    setIsDirty(true);
    const section = sections[sectionIndex];
    const firstApi = availableApis[0];
    const newField: TemplateField = {
      id: `field_${Date.now()}`,
      api_source: firstApi ? firstApi.name : '',
      api_source_id: firstApi?.id,
      field_path: '',
      display_name: '',
      order: section.fields.length + 1,
      merge_strategy: 'first',
      hidden_in_report: false
    };
    
    updateSection(sectionIndex, {
      fields: [...section.fields, newField]
    });
  };

  const updateField = (sectionIndex: number, fieldIndex: number, updates: Partial<TemplateField>) => {
    setIsDirty(true);
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return {
        ...s,
        fields: s.fields.map((f, j) => 
          j === fieldIndex ? { ...f, ...updates } : f
        )
      };
    }));
  };

  const moveField = (sectionIndex: number, fieldIndex: number, direction: 'up' | 'down') => {
    setIsDirty(true);
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      const newFields = [...s.fields];
      const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
      
      if (newIndex < 0 || newIndex >= newFields.length) return s;
      
      [newFields[fieldIndex], newFields[newIndex]] = [newFields[newIndex], newFields[fieldIndex]];
      // Update orders
      newFields.forEach((f, j) => { f.order = j + 1; });
      
      return { ...s, fields: newFields };
    }));
  };

  const handleCancelWithWarning = () => {
    if (isDirty && !window.confirm('You have unsaved changes in this template. Are you sure you want to discard them and go back?')) {
      return;
    }
    onCancel();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }

    if (!selectedParameterType) {
      showToast('Parameter type is required', 'error');
      return;
    }

    if (sections.length === 0) {
      showToast('At least one section is required', 'error');
      return;
    }

    // Validate sections have fields
    for (const section of sections) {
      if (section.fields.length === 0) {
        showToast(`Section "${section.heading}" must have at least one field`, 'error');
        return;
      }
      
      for (const field of section.fields) {
        if (!field.api_source || !field.field_path || !field.display_name) {
          showToast('All fields must have API source, field path, and display name', 'error');
          return;
        }
      }
    }

    try {
      setLoading(true);
      const templateData: any = {
        name: name.trim(),
        description: description.trim(),
        parameter_type_id: selectedParameterType,
        configuration: {
          sections: sections.map(s => ({
            heading: s.heading,
            order: s.order,
            collapsible: s.collapsible || false,
            fields: s.fields.map(f => ({
              api_source: f.api_source,
              api_source_id: f.api_source_id,
              field_path: f.field_path,
              display_name: f.display_name,
              order: f.order,
              merge_strategy: f.merge_strategy || 'first',
              hidden_in_report: f.hidden_in_report ?? false,
              item_fields: f.item_fields && f.item_fields.length > 0 ? f.item_fields : undefined
            }))
          }))
        },
        is_default: isDefault,
        is_active: isActive
      };

      await onSave(templateData);
      setIsDirty(false);
    } catch (err: any) {
      showToast(err.message || 'Failed to save template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputBase = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors shadow-sm';
  const labelBase = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';

  return (
    <div className="flex flex-col min-h-0 pb-24">
      <div className="space-y-8">
      <Card className="overflow-hidden">
        <div className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Template Details</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Basic information for your report template</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelBase}>Template Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                  setIsDirty(true);
                }}
                placeholder="e.g., Phone Number Template"
                className={inputBase}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Parameter Type *
                </label>
                {template && (
                  <span className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1 font-normal">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Locked for existing template
                  </span>
                )}
              </div>
              {template && (parameterTypesLoading || !parameterTypes.some((pt) => pt.id === selectedParameterType)) ? (
                <div className={`${inputBase} bg-gray-50 dark:bg-gray-800 flex items-center text-gray-700 dark:text-gray-300`}>
                  {template.parameter_type_detail?.display_name ?? (template as { parameter_type_name?: string }).parameter_type_name ?? (parameterTypesLoading ? 'Loading...' : 'N/A')}
                </div>
              ) : (
                <select
                  value={selectedParameterType}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setSelectedParameterType(Number(e.target.value));
                    setIsDirty(true);
                  }}
                  className={`${inputBase} ${template ? 'opacity-75 cursor-not-allowed bg-gray-50 dark:bg-gray-800' : ''}`}
                  disabled={!!template}
                >
                  <option value={0}>Select Parameter Type</option>
                  {parameterTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.display_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div>
            <label className={labelBase}>Description</label>
            <textarea
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setDescription(e.target.value);
                setIsDirty(true);
              }}
              placeholder="Optional description of this template"
              rows={2}
              className={`${inputBase} resize-none`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsActive(e.target.checked);
                  setIsDirty(true);
                }}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                  Active Template
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  When active, this template is available for search report generation.
                </span>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsDefault(e.target.checked);
                  setIsDirty(true);
                }}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-gray-900 dark:text-white block">
                  Default Template
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Automatically applied when generating reports for this parameter type.
                </span>
              </div>
            </label>
          </div>
        </div>
      </Card>

      {/* Parameter & Section Quick Actions */}
      {selectedParameterType > 0 ? (
        <Card className="overflow-hidden border border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-r from-indigo-50/70 via-purple-50/40 to-blue-50/50 dark:from-indigo-950/20 dark:via-purple-950/10 dark:to-blue-950/20 shadow-sm">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>Parameter & Section Management</span>
                  <Badge variant="default" size="sm">
                    {availableApis.length} APIs Connected
                  </Badge>
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Browse and pick response parameters from your APIs or create custom report sections.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 shrink-0">
              <Button
                type="button"
                onClick={() => {
                  setPickerTargetSectionId(sections[0]?.id);
                  setIsPickerOpen(true);
                }}
                className="gap-2 shadow-sm bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Select Parameters
              </Button>
              <Button
                type="button"
                onClick={addSection}
                variant="outline"
                className="gap-2 bg-white dark:bg-gray-800"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Add Section
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
          <div className="p-6 text-sm text-amber-800 dark:text-amber-200">
            Select a parameter type above to load APIs linked to that search type.
          </div>
        </Card>
      )}

      {/* Floating / Sticky Bulk Actions Toolbar */}
      {selectedFieldIds.size > 0 && (
        <div className="sticky top-4 z-20 bg-indigo-900 text-white p-3.5 rounded-xl shadow-xl flex flex-wrap items-center justify-between gap-3 border border-indigo-700">
          <div className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-indigo-700 text-white font-bold text-xs flex items-center justify-center">
              {selectedFieldIds.size}
            </span>
            <span className="text-sm font-medium">field(s) selected</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-indigo-800/80 rounded-lg px-2 py-1">
              <span className="text-xs text-indigo-200">Move to:</span>
              <select
                value={bulkTargetSectionId}
                onChange={(e) => setBulkTargetSectionId(e.target.value)}
                className="text-xs bg-indigo-700 border border-indigo-600 rounded px-2 py-1 text-white focus:ring-1 focus:ring-white"
              >
                <option value="">Select Section...</option>
                {sections.map((s, idx) => (
                  <option key={s.id || idx} value={s.id}>
                    {s.heading}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!bulkTargetSectionId}
                onClick={handleBulkMoveFields}
                className="text-xs py-1 px-2.5 bg-white text-indigo-900 hover:bg-indigo-50 border-transparent font-medium"
              >
                Apply
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleBulkToggleHide(false)}
              className="text-xs py-1 px-2 text-white border-indigo-700 hover:bg-indigo-800"
              title="Make selected fields visible in report"
            >
              Unhide
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleBulkToggleHide(true)}
              className="text-xs py-1 px-2 text-white border-indigo-700 hover:bg-indigo-800"
              title="Hide selected fields in report"
            >
              Hide
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleBulkDeleteFields}
              className="text-xs py-1 px-2 text-red-300 border-red-800/60 hover:bg-red-950/60"
            >
              Delete
            </Button>
            <button
              type="button"
              onClick={() => setSelectedFieldIds(new Set())}
              className="text-xs text-indigo-300 hover:text-white ml-2 underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Sections</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} · expand to edit fields
            </p>
          </div>
          <Button onClick={addSection} variant="outline" size="sm" className="gap-2 shrink-0" disabled={selectedParameterType <= 0}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Section
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">Show fields:</span>
          <div className="inline-flex flex-wrap rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 p-0.5 gap-0.5">
            {([
              { id: 'all' as const, label: 'Show all', count: fieldVisibilityCounts.all, title: 'Show all fields' },
              { id: 'hide_checked' as const, label: 'Hide checked', count: fieldVisibilityCounts.hideChecked, title: 'Show only fields with Hide checked' },
              { id: 'unhide_checked' as const, label: 'Unhide checked', count: fieldVisibilityCounts.unhideChecked, title: 'Show only fields with Hide unchecked (visible in report)' },
            ]).map((option) => (
              <button
                key={option.id}
                type="button"
                title={option.title}
                onClick={() => setFieldVisibilityFilter(option.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  fieldVisibilityFilter === option.id
                    ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 font-medium shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
                <span className={`ml-1.5 text-xs ${
                  fieldVisibilityFilter === option.id
                    ? 'text-indigo-500 dark:text-indigo-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  ({option.count})
                </span>
              </button>
            ))}
          </div>
        </div>
        {availableApis.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">Section API:</span>
            <select
              value={sectionApiFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const val = e.target.value;
                setSectionApiFilter(val ? Number(val) : '');
              }}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 max-w-md"
            >
              <option value="">All APIs for this parameter type</option>
              {availableApis.map((api) => (
                <option key={api.id} value={api.id}>
                  {api.description || api.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 sm:ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAllSections}
                className="text-xs py-1 px-2.5"
                title="Expand all sections to view and edit fields"
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAllSections}
                className="text-xs py-1 px-2.5"
                title="Collapse all sections to high-level outline"
              >
                Collapse All
              </Button>
            </div>
          </div>
        )}
        {!availableApis.length && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAllSections}
              className="text-xs py-1 px-2.5"
            >
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAllSections}
              className="text-xs py-1 px-2.5"
            >
              Collapse All
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {filteredSections.length === 0 && (
          <Card>
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {sections.length === 0
                ? 'No sections yet. Add a section or import from display parameters.'
                : 'No sections match the selected API filter.'}
            </div>
          </Card>
        )}
        {filteredSections.map((section) => {
          const sectionIndex = sections.findIndex((s) => s.id === section.id);
          if (sectionIndex < 0) return null;
          const sectionId = section.id || `section_${sectionIndex}`;
          const isExpanded = expandedSectionIds.has(sectionId);
          const visibleFields = section.fields.filter(matchesFieldVisibilityFilter);
          const hiddenFieldCount = section.fields.length - visibleFields.length;

          return (
          <div
            key={sectionId}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              if (dragData?.type === 'section') {
                const target = e.currentTarget as HTMLElement;
                target.classList.add('border-blue-400');
              }
            }}
            onDragLeave={(e: React.DragEvent) => {
              const target = e.currentTarget as HTMLElement;
              target.classList.remove('border-blue-400');
            }}
            onDrop={(e: React.DragEvent) => {
              e.preventDefault();
              const target = e.currentTarget as HTMLElement;
              target.classList.remove('border-blue-400');
              
              if (dragData?.type === 'section') {
                const draggedSectionId = dragData.sectionId;
                const targetSectionId = sectionId;
                
                if (draggedSectionId && draggedSectionId !== targetSectionId) {
                  setSections(prev => {
                    const newSections = [...prev];
                    const draggedIndex = newSections.findIndex(s => s.id === draggedSectionId);
                    const targetIndex = newSections.findIndex(s => s.id === targetSectionId);
                    
                    if (draggedIndex >= 0 && targetIndex >= 0) {
                      const [dragged] = newSections.splice(draggedIndex, 1);
                      newSections.splice(targetIndex, 0, dragged);
                      newSections.forEach((s, i) => { s.order = i + 1; });
                    }
                    
                    return newSections;
                  });
                }
              }
              setDragData(null);
            }}
            className="transition-all"
          >
          <Card className="hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
            <div className="p-5 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    draggable
                    onDragStart={(e: React.DragEvent) => {
                      e.stopPropagation();
                      setDragData({ type: 'section', sectionId });
                      const target = e.currentTarget as HTMLElement;
                      target.style.opacity = '0.5';
                    }}
                    onDragEnd={(e: React.DragEvent) => {
                      const target = e.currentTarget as HTMLElement;
                      target.style.opacity = '1';
                    }}
                    className="shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center cursor-grab active:cursor-grabbing"
                    title="Drag to reorder section"
                  >
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <title>Drag to reorder section</title>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={section.heading}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSection(sectionIndex, { heading: e.target.value })}
                    className={`flex-1 min-w-0 ${inputBase} font-semibold`}
                    placeholder="Section Heading"
                  />
                  <Badge variant="default" size="sm">
                    {section.fields.length} fields
                  </Badge>
                  {hiddenFieldCount > 0 && fieldVisibilityFilter !== 'all' && (
                    <span className="text-xs text-gray-500">({visibleFields.length} shown)</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSectionExpanded(sectionId);
                    }}
                    className="gap-1"
                  >
                    {isExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPickerTargetSectionId(sectionId);
                      setIsPickerOpen(true);
                    }}
                    className="gap-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100"
                    title="Pick parameters from APIs into this section"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Pick Parameters
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addField(sectionIndex)}
                    className="gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Field
                  </Button>
                  {section.fields.length > 0 && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none px-1" title="Select all fields in this section">
                      <input
                        type="checkbox"
                        checked={section.fields.length > 0 && section.fields.every((f, idx) => selectedFieldIds.has(f.id || `field_${idx}`))}
                        onChange={() => handleSelectAllSectionFields(section)}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                      />
                      <span>Select All</span>
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer px-1">
                    <input
                      type="checkbox"
                      checked={section.collapsible || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSection(sectionIndex, { collapsible: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                    />
                    Collapsible
                  </label>
                  <div className="h-4 w-px bg-gray-200 dark:bg-gray-600" />
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveSection(sectionIndex, 'up')}
                      disabled={sectionIndex === 0}
                      className="p-1.5"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => moveSection(sectionIndex, 'down')}
                      disabled={sectionIndex === sections.length - 1}
                      className="p-1.5"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => deleteSection(sectionIndex)}
                      className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      title="Delete section"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </div>

              {isExpanded && (
              <div 
                className="space-y-3 border-t border-gray-200 dark:border-gray-600 pt-4"
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault();
                  if (dragData?.type === 'field') {
                    const target = e.currentTarget as HTMLElement;
                    target.classList.add('bg-blue-50 dark:bg-blue-900/20', 'border-blue-300', 'border-2', 'border-dashed', 'rounded-lg');
                  }
                }}
                onDragLeave={(e: React.DragEvent) => {
                  const target = e.currentTarget as HTMLElement;
                  target.classList.remove('bg-blue-50 dark:bg-blue-900/20', 'border-blue-300', 'border-2', 'border-dashed', 'rounded-lg');
                }}
                onDrop={(e: React.DragEvent) => {
                  e.preventDefault();
                  const target = e.currentTarget as HTMLElement;
                  target.classList.remove('bg-blue-50 dark:bg-blue-900/20', 'border-blue-300', 'border-2', 'border-dashed', 'rounded-lg');
                  
                  if (dragData?.type === 'field') {
                    const { fieldId, fromSectionId } = dragData;
                    const toSectionId = sectionId;
                    
                    // Only move if dropping in a different section or empty area
                    if (fromSectionId !== toSectionId || section.fields.length === 0) {
                      setSections(prev => {
                        const newSections = prev.map(s => ({ ...s, fields: [...s.fields] }));
                        const fromSection = newSections.find(s => s.id === fromSectionId);
                        const toSection = newSections.find(s => s.id === toSectionId);
                        
                        if (!fromSection || !toSection) return prev;
                        
                        const fieldIndex = fromSection.fields.findIndex(f => f.id === fieldId);
                        if (fieldIndex < 0) return prev;
                        
                        const [movedField] = fromSection.fields.splice(fieldIndex, 1);
                        
                        // Add to end of target section if dropping on empty area
                        toSection.fields.push(movedField);
                        
                        // Update orders
                        fromSection.fields.forEach((f, i) => { f.order = i + 1; });
                        toSection.fields.forEach((f, i) => { f.order = i + 1; });
                        
                        return newSections;
                      });
                    }
                  }
                  setDragData(null);
                }}
              >
                {section.fields.length === 0 && (
                  <div 
                    className={`text-sm text-center py-8 rounded-xl transition-all border-2 border-dashed ${
                      dragData?.type === 'field' 
                        ? 'text-indigo-600 dark:text-indigo-400 font-medium border-indigo-300 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' 
                        : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 dark:bg-gray-800/30'
                    }`}
                  >
                    {dragData?.type === 'field' 
                      ? 'Drop field here to add to this section' 
                      : (
                        <div className="flex flex-col items-center gap-3">
                          <p className="text-gray-600 dark:text-gray-400">No fields in this section yet.</p>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setPickerTargetSectionId(sectionId);
                                setIsPickerOpen(true);
                              }}
                              className="gap-1.5 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Pick Parameters
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addField(sectionIndex)}
                              className="gap-1.5 text-xs"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Field
                            </Button>
                          </div>
                        </div>
                      )}
                  </div>
                )}
                {section.fields.length > 0 &&
                  section.fields.filter(matchesFieldVisibilityFilter).length === 0 && (
                  <div className="text-sm text-center py-6 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/30 text-gray-500 dark:text-gray-400">
                    No fields match the current filter in this section.
                  </div>
                )}
                {section.fields.map((field, fieldIndex) => {
                  if (!matchesFieldVisibilityFilter(field)) return null;

                  const fieldId = field.id || `field_${sectionIndex}_${fieldIndex}`;
                  const isFieldSelected = selectedFieldIds.has(field.id || fieldId);
                  
                  return (
                  <div key={fieldId}>
                    <div 
                    id={`field-row-${fieldId}`}
                    onDragOver={(e: React.DragEvent) => {
                      e.preventDefault();
                      if (dragData?.type === 'field') {
                        const target = e.currentTarget as HTMLElement;
                        target.classList.add('border-blue-400');
                      }
                    }}
                    onDragLeave={(e: React.DragEvent) => {
                      const target = e.currentTarget as HTMLElement;
                      target.classList.remove('border-blue-400');
                    }}
                    onDrop={(e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent bubbling to section container
                      const target = e.currentTarget as HTMLElement;
                      target.classList.remove('border-blue-400');
                      
                      if (dragData?.type === 'field') {
                        const { fieldId: draggedFId, fromSectionId } = dragData;
                        const toSectionId = sectionId;
                        
                        setSections(prev => {
                          const newSections = prev.map(s => ({ ...s, fields: [...s.fields] }));
                          const fromSection = newSections.find(s => s.id === fromSectionId);
                          const toSection = newSections.find(s => s.id === toSectionId);
                          
                          if (!fromSection || !toSection) return prev;
                          
                          const fIndex = fromSection.fields.findIndex(f => f.id === draggedFId);
                          if (fIndex < 0) return prev;
                          
                          const [movedField] = fromSection.fields.splice(fIndex, 1);
                          
                          // Find drop position
                          const dropIndex = toSection.fields.findIndex(f => f.id === field.id);
                          const insertIndex = dropIndex < 0 ? toSection.fields.length : dropIndex;
                          
                          toSection.fields.splice(insertIndex, 0, movedField);
                          
                          // Update orders
                          fromSection.fields.forEach((f, i) => { f.order = i + 1; });
                          toSection.fields.forEach((f, i) => { f.order = i + 1; });
                          
                          return newSections;
                        });
                      }
                      setDragData(null);
                    }}
                    className={`grid grid-cols-12 gap-3 items-center p-3.5 rounded-xl border-2 transition-all ${
                      isFieldSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-400 dark:border-indigo-600 ring-1 ring-indigo-400'
                        : !isFieldApiRelevant(field)
                        ? 'bg-amber-50/80 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-transparent hover:border-indigo-200 dark:hover:border-indigo-800/50'
                    }`}
                  >
                    <div className="col-span-1 flex items-center justify-center gap-1.5">
                      <div
                        draggable
                        onDragStart={(e: React.DragEvent) => {
                          e.stopPropagation();
                          setDragData({ 
                            type: 'field', 
                            fieldId: field.id || fieldId, 
                            fromSectionId: sectionId 
                          });
                          const parentCard = document.getElementById(`field-row-${fieldId}`);
                          if (parentCard) parentCard.style.opacity = '0.5';
                        }}
                        onDragEnd={() => {
                          const parentCard = document.getElementById(`field-row-${fieldId}`);
                          if (parentCard) parentCard.style.opacity = '1';
                        }}
                        className="p-1 rounded cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700 shrink-0"
                        title="Drag to reorder field"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="checkbox"
                          checked={isFieldSelected}
                          onChange={() => toggleSelectField(field.id || fieldId)}
                          className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                          title="Select for bulk action"
                        />
                        <label className="flex items-center gap-0.5 cursor-pointer" title="Hide this field in the report">
                          <input
                            type="checkbox"
                            checked={field.hidden_in_report ?? false}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              updateField(sectionIndex, fieldIndex, { hidden_in_report: e.target.checked })
                            }
                            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                          />
                          <span className="text-[9px] text-gray-500">Hide</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-span-3">
                      <select
                        value={field.api_source}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const selectedApiName = e.target.value;
                          const selectedApi = availableApis.find(a => a.name === selectedApiName);
                          updateField(sectionIndex, fieldIndex, {
                            api_source: selectedApiName,
                            api_source_id: selectedApi?.id
                          });
                        }}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Select API</option>
                        {availableApis.map((api) => (
                          <option key={api.id} value={api.name}>
                            {api.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      {field.api_source && availableFields[field.api_source] && availableFields[field.api_source].length > 0 ? (
                        <select
                          value={field.field_path}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateField(sectionIndex, fieldIndex, { field_path: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Select Field</option>
                          {availableFields[field.api_source].map((fieldPath) => (
                            <option key={fieldPath} value={fieldPath}>
                              {fieldPath}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={field.field_path}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField(sectionIndex, fieldIndex, { field_path: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-xs"
                          placeholder="field_path (e.g. user.name)"
                          disabled={!field.api_source}
                        />
                      )}
                    </div>
                    <div className="col-span-3">
                      <input
                        type="text"
                        value={field.display_name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField(sectionIndex, fieldIndex, { display_name: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-medium"
                        placeholder="Display Name"
                      />
                    </div>
                    <div className="col-span-1">
                      <select
                        value={field.merge_strategy || 'first'}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateField(sectionIndex, fieldIndex, { merge_strategy: e.target.value as any })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-1.5 py-1.5 text-xs bg-white dark:bg-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        title="Merge Strategy"
                      >
                        <option value="first">First</option>
                        <option value="all">All</option>
                        <option value="best">Best</option>
                      </select>
                    </div>
                    <div className="col-span-1 flex flex-col sm:flex-row gap-1 justify-center items-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveField(sectionIndex, fieldIndex, 'up')}
                        disabled={fieldIndex === 0}
                        className="p-1"
                        title="Move up"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveField(sectionIndex, fieldIndex, 'down')}
                        disabled={fieldIndex === section.fields.length - 1}
                        className="p-1"
                        title="Move down"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => deleteField(sectionIndex, fieldIndex)}
                        className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Delete field"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                  {/* Array item columns: when field_path points to an array of objects, show these as table columns */}
                  <div className="mt-3 pl-4 border-l-2 border-amber-200 dark:border-amber-800 rounded-r bg-amber-50/50 dark:bg-amber-900/10 p-3">
                    <div className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2">
                      Array item columns (optional)
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      If this field is an array of objects, add paths relative to each item (e.g. name, profile.email) to show as table columns.
                    </p>
                    {(field.item_fields || []).map((col, colIndex) => (
                      <div key={colIndex} className="flex gap-2 items-center mb-2">
                        <input
                          type="text"
                          value={col.path}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = [...(field.item_fields || [])];
                            next[colIndex] = { ...next[colIndex], path: e.target.value };
                            updateField(sectionIndex, fieldIndex, { item_fields: next });
                          }}
                          className="flex-1 min-w-0 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700"
                          placeholder="Path (e.g. name, profile.email)"
                        />
                        <input
                          type="text"
                          value={col.display_name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const next = [...(field.item_fields || [])];
                            next[colIndex] = { ...next[colIndex], display_name: e.target.value };
                            updateField(sectionIndex, fieldIndex, { item_fields: next });
                          }}
                          className="w-28 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700"
                          placeholder="Column label"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const next = (field.item_fields || []).filter((_, i) => i !== colIndex);
                            updateField(sectionIndex, fieldIndex, { item_fields: next.length ? next : undefined });
                          }}
                          className="p-1.5 text-red-600 dark:text-red-400 shrink-0"
                          title="Remove column"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => updateField(sectionIndex, fieldIndex, {
                        item_fields: [...(field.item_fields || []), { path: '', display_name: '' }]
                      })}
                      className="gap-1 text-xs"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add column
                    </Button>
                  </div>
                  </div>
                  );
                })}
              </div>
              )}
            </div>
          </Card>
          </div>
          );
        })}
      </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 flex flex-col-reverse sm:flex-row justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.3)]">
        <Button variant="outline" onClick={handleCancelWithWarning} disabled={loading} className="gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={loading} className="gap-2">
          {loading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {template ? 'Update Template' : 'Create Template'}
            </>
          )}
        </Button>
      </div>

      {/* Parameter Picker Modal */}
      <ParameterPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        availableApis={availableApis}
        apiDisplayParams={apiDisplayParams}
        availableFields={availableFields}
        sections={sections}
        initialTargetSectionId={pickerTargetSectionId}
        onAddFields={handleAddFieldsFromPicker}
      />
    </div>
  );
};

